'use client'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { useMutation, useQuery } from '@apollo/client'
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import {
  CREATE_ADDITIONAL_COMPONENT_GROUP,
  GET_ADDITIONAL_COMPONENT_GROUP,
  UPDATE_ADDITIONAL_COMPONENT_GROUP,
} from './additionalComponentGroupQueries'
import { useAdditionalComponentDelete } from './useAdditionalComponentDelete'
import { useLocation, useNavigate } from 'react-router-dom'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { Icons } from '@/components/shared/icons'

const groupUITypeOptions = [
  {
    id: 'classic',
    name: 'Classic',
  },
  {
    id: 'drawer',
    name: 'Using side drawer',
  },
  {
    id: 'drawer_with_zoom',
    name: 'Using side drawer with zoom',
  },
  {
    id: 'drawer_wide',
    name: 'Using side drawer + wide view',
  },
  {
    id: 'drawer_with_zoom_wide',
    name: 'Using side drawer + zoom, wide view',
  },
  {
    id: 'hidden',
    name: 'Hidden',
  },
]

const dimensionsConstructor = {
  armrest_height: 'Armrest height',
  armrest_length: 'Armrest length',
  armrest_width: 'Armrest width',
  bedbox_height: 'Bedbox height',
  bedbox_length: 'Bedbox length',
  bedbox_width: 'Bedbox width',
  covered_side: 'Covered side',
  frame_thickness: 'Frame thickness',
  headboard_height: 'Headboard height',
  headboard_width: 'Headboard width',
  height: 'Height',
  leg_height: 'Leg height',
  length: 'Length',
  lying_part_height: 'Lying part height',
  mattress_height: 'Mattress height',
  mattress_length: 'Mattress length',
  mattress_overlay_height: 'Mattress overlay height',
  mattress_width: 'Mattress width',
  radius: 'Radius',
  seat_depth: 'Seat depth',
  seat_height: 'Seat height',
  seat_width: 'Seat width',
  shade_height: 'Shade height',
  shade_radius: 'Shade radius',
  table_extended_lengh: 'Table extended length',
  table_leg_width: 'Table leg width',
  table_top_thickness: 'Table top thickness',
  weight: 'Weight',
  width: 'Width',
}

const formSchema = z.object({
  manufacturerId: z.number().min(1, 'Manufacturer is required'),
  code: z.string().optional(),
  ui_type: z.string().optional(),
  use_fabric_prices_for_components: z.boolean().optional(),
  hide_components_without_price: z.boolean().optional(),
  enable_custom_dimensions_for_components: z.boolean().optional(),
  enabled_dimensions_in_group: z.array(z.string()).optional(),
  order: z.number().optional(),
  additional_component_group_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        language: z.string(),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter(item => item.name?.trim() !== '')
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language profile needs to be filled',
            path: [`[${index}].name`],
          })
        })
      }
      return filteredItems
    }),
})

interface MyFormProps {
  data: {
    id: number
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function AdditionalComponentGroupForm({
  data,
  onDirtyChange,
  onRequestClose,
}: MyFormProps) {
  const { allLanguages, languageId } = useLanguage()
  const [id, setId] = useState(data?.id)

  const {
    data: queryData,
    loading: loadingData,
    error,
  } = useQuery(GET_ADDITIONAL_COMPONENT_GROUP, {
    variables: {
      id: id,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const additionalComponentGroupData =
    queryData?.findUniqueAdditionalComponentGroup

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      manufacturerId: undefined,
      additional_component_group_profiles: [],
      code: '',
      ui_type: '',
      use_fabric_prices_for_components: false,
      hide_components_without_price: false,
      enable_custom_dimensions_for_components: false,
      enabled_dimensions_in_group: [],
      order: undefined,
    },
  })

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = form

  const [updateAdditionalComponentGroup] = useMutation(
    UPDATE_ADDITIONAL_COMPONENT_GROUP,
    {
      onError: e => {
        toast.error('Failed to update additional component group')
      },
      onCompleted: e => {
        toast.success('Additional component group updated successfully')
      },
      refetchQueries: [
        'GET_ADDITIONAL_COMPONENT_GROUPS',
        'GET_ADDITIONAL_COMPONENT_GROUP',
      ],
    }
  )

  const [createAdditionalComponentGroup] = useMutation(
    CREATE_ADDITIONAL_COMPONENT_GROUP,
    {
      onError: e => {
        toast.error('Failed to create additional component group')
      },
      onCompleted: response => {
        toast.success('Additional component group created successfully')
        const newId = response.createSingleAdditionalComponentGroup.id
        setId(newId)
      },
      refetchQueries: ['GET_ADDITIONAL_COMPONENT_GROUPS'],
    }
  )

  const { confirmDelete } = useAdditionalComponentDelete('group')
  const navigate = useNavigate()
  const location = useLocation()

  const handleDelete = () => {
    if (!id) return
    confirmDelete([id], () => {
      onRequestClose?.()
      // Opened from the group's own details page: that page is gone now.
      const detailsPath = `/products/additional-component-groups/${id}`
      if (location.pathname.replace(/\/$/, '').endsWith(detailsPath)) {
        navigate('/products/additional-component-groups')
      }
    })
  }

  function onSubmit(values: z.infer<typeof formSchema>) {
    if (id) {
      updateAdditionalComponentGroup({
        variables: {
          where: { id: additionalComponentGroupData.id },
          additional_component_group_profiles:
            values.additional_component_group_profiles,
          code: values.code,
          ui_type: values.ui_type,
          use_fabric_prices_for_components:
            values.use_fabric_prices_for_components,
          hide_components_without_price: values.hide_components_without_price,
          enable_custom_dimensions_for_components:
            values.enable_custom_dimensions_for_components,
          enabled_dimensions_in_group: values.enabled_dimensions_in_group,
        },
      })
    } else {
      createAdditionalComponentGroup({
        variables: {
          manufacturerId: values.manufacturerId,
          additional_component_group_profiles:
            values.additional_component_group_profiles.map(p => ({
              name: p.name,
              description: p.description,
              language: p.language,
            })),
          code: values.code,
          ui_type: values.ui_type,
          use_fabric_prices_for_components:
            values.use_fabric_prices_for_components,
          hide_components_without_price: values.hide_components_without_price,
          enable_custom_dimensions_for_components:
            values.enable_custom_dimensions_for_components,
          enabled_dimensions_in_group: values.enabled_dimensions_in_group,
          order: values.order,
        },
      })
    }
  }

  useEffect(() => {
    if (!loadingData) {
      if (additionalComponentGroupData) {
        // Reset with existing data, ensuring all languages have profiles
        const existingProfiles =
          additionalComponentGroupData.additional_component_group_profiles?.filter(
            p => p.language && allLanguages.includes(p.language)
          ) ?? []

        // Create profiles for all languages, using existing data where available
        const allProfiles = allLanguages.map(lang => {
          const existingProfile = existingProfiles.find(
            p => p.language === lang
          )
          if (existingProfile) {
            return {
              id: existingProfile.id,
              name: existingProfile.name ?? '',
              description: existingProfile.description ?? '',
              language: existingProfile.language,
            }
          } else {
            // Create new profile for missing language
            return {
              name: '',
              description: '',
              language: lang,
            }
          }
        })

        reset({
          manufacturerId:
            additionalComponentGroupData.manufacturerId ?? undefined,
          additional_component_group_profiles: allProfiles,
          code: additionalComponentGroupData.code ?? '',
          ui_type: additionalComponentGroupData.ui_type ?? '',
          use_fabric_prices_for_components:
            additionalComponentGroupData.use_fabric_prices_for_components ??
            false,
          hide_components_without_price:
            additionalComponentGroupData.hide_components_without_price ?? false,
          enable_custom_dimensions_for_components:
            additionalComponentGroupData.enable_custom_dimensions_for_components ??
            false,
          enabled_dimensions_in_group:
            additionalComponentGroupData.enabled_dimensions_in_group ?? [],
          order: additionalComponentGroupData.order ?? undefined,
        })
      } else {
        // For new forms, initialize with profiles for all languages
        reset({
          manufacturerId: undefined,
          additional_component_group_profiles: allLanguages.map(lang => ({
            name: '',
            description: '',
            language: lang,
          })),
          code: '',
          ui_type: '',
          use_fabric_prices_for_components: false,
          hide_components_without_price: false,
          enable_custom_dimensions_for_components: false,
          enabled_dimensions_in_group: [],
          order: undefined,
        })
      }
    }
  }, [additionalComponentGroupData, loadingData, allLanguages, reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  console.log('Form errors:', form.formState.errors)

  return (
    <>
      <SheetContent className="sheet-content flex flex-col margin">
        <SheetHeader className="flex-shrink-0">
          <div className="flex justify-between items-center">
            <SheetTitle>Additional Component Group</SheetTitle>
            <LanguageSwitcher />
          </div>
        </SheetHeader>
        <SheetScrollableContent>
          <Form {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4 max-w-3xl mx-auto py-5"
            >
              <div className="grid gap-4 py-4">
                <InputFieldContainer
                  label="Manufacturer ID"
                  description="Required. The manufacturer this component group belongs to."
                  control={control}
                  name="manufacturerId"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Input
                          placeholder="Manufacturer ID"
                          type="number"
                          value={field.value?.toString() || ''}
                          onChange={e =>
                            field.onChange(
                              e.target.value
                                ? parseInt(e.target.value, 10)
                                : undefined
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <InputFieldContainer
                  label="Group Name"
                  description="This is the display name for the additional component group."
                  control={control}
                  name={`additional_component_group_profiles.${languageId}.name`}
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Input
                          placeholder="Group Name"
                          type="text"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <InputFieldContainer
                  label="Description"
                  control={control}
                  name={`additional_component_group_profiles.${languageId}.description`}
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Textarea
                          placeholder="Description"
                          className="resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <InputFieldContainer
                  label="Group Code"
                  description="Optional unique identifier for the group."
                  control={control}
                  name="code"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Input
                          placeholder="Group Code"
                          type="text"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <InputFieldContainer
                  label="UI Type"
                  description="How this group will be displayed in the product page"
                  control={control}
                  name="ui_type"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select UI type" />
                          </SelectTrigger>
                          <SelectContent>
                            {groupUITypeOptions.map(option => (
                              <SelectItem key={option.id} value={option.id}>
                                {option.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <InputFieldContainer
                  label="Order"
                  description="Optional. Display order for this component group."
                  control={control}
                  name="order"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Input
                          placeholder="Order"
                          type="number"
                          value={field.value?.toString() || ''}
                          onChange={e =>
                            field.onChange(
                              e.target.value
                                ? parseInt(e.target.value, 10)
                                : undefined
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="use_fabric_prices_for_components"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/50">
                        <div className="space-y-0.5">
                          <FormLabel className="text-sm font-medium">
                            Use Fabric Prices for Components
                          </FormLabel>
                          <div className="text-xs text-muted-foreground">
                            Apply fabric-based pricing to components (e.g., bed
                            headboards with premium fabric upcharges)
                          </div>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="hide_components_without_price"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/50">
                        <div className="space-y-0.5">
                          <FormLabel className="text-sm font-medium">
                            Hide Components Without Prices
                          </FormLabel>
                          <div className="text-xs text-muted-foreground">
                            Hide components that don't have pricing information
                            available
                            <br />
                            <strong>Note:</strong> It keeps components with code
                            "0" even if they don't have a price.
                          </div>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="enable_custom_dimensions_for_components"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between rounded-lg border p-4 bg-muted/50">
                        <div className="space-y-0.5">
                          <FormLabel className="text-sm font-medium">
                            Enable Custom Dimensions for Components
                          </FormLabel>
                          <div className="text-xs text-muted-foreground">
                            Allow component selection to override product
                            dimensions automatically
                          </div>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {watch('enable_custom_dimensions_for_components') && (
                  <FormField
                    control={control}
                    name="enabled_dimensions_in_group"
                    render={({ field }) => (
                      <FormItem>
                        <div className="rounded-lg border p-4 bg-muted/50">
                          <FormLabel className="text-sm font-medium">
                            Enabled Dimensions in Group
                          </FormLabel>
                          <div className="text-xs text-muted-foreground mb-4">
                            Select which dimensions should be available for
                            components in this group
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {Object.entries(dimensionsConstructor)
                              .sort(([, a], [, b]) => a.localeCompare(b))
                              .map(([key, label]) => (
                                <div
                                  key={key}
                                  className="flex items-center space-x-2"
                                >
                                  <Checkbox
                                    id={key}
                                    checked={
                                      field.value?.includes(key) || false
                                    }
                                    onCheckedChange={checked => {
                                      const currentValue = field.value || []
                                      if (checked) {
                                        field.onChange([...currentValue, key])
                                      } else {
                                        field.onChange(
                                          currentValue.filter(
                                            item => item !== key
                                          )
                                        )
                                      }
                                    }}
                                  />
                                  <label
                                    htmlFor={key}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                  >
                                    {label}
                                  </label>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <div className="flex gap-2">
                  {id && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleDelete}
                      className="shrink-0"
                    >
                      <Icons.trash className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                  <Button
                    type="submit"
                    disabled={loadingData}
                    className="flex-1"
                  >
                    {id ? 'Update' : 'Create'}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </SheetScrollableContent>
      </SheetContent>
    </>
  )
}
